<template>
  <Teleport
    to="body"
    :disabled="!centered"
  >
    <div
      v-if="isVisible"
      class="toast"
      :class="[type, { 'slide-in': isVisible, 'toast--centered': centered }]"
    >
      <div class="toast-icon">
        <span v-if="type === 'success'">✅</span>
        <span v-else-if="type === 'error'">❌</span>
        <span v-else-if="type === 'info'">ℹ️</span>
      </div>
      <div class="toast-content">
        <div class="toast-title">
          {{ title }}
        </div>
        <div
          v-if="message"
          class="toast-message"
        >
          {{ message }}
        </div>
      </div>
      <div
        class="progress-bar"
        :style="{ width: progress + '%' }"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, defineProps } from 'vue';

const props = defineProps<{
  title: string;
  message: string;
  duration?: number;
  type?: 'success' | 'error' | 'info';
  /** Fixed overlay centered in the viewport (e.g. copy confirmation). */
  centered?: boolean;
}>();

const isVisible = ref(false);
const progress = ref(100);
let progressInterval: number;

onMounted(() => {
  isVisible.value = true;
  const duration = props.duration || 5000;
  const updateInterval = 10;
  const steps = duration / updateInterval;
  let currentStep = 0;

  progressInterval = setInterval(() => {
    currentStep++;
    progress.value = 100 - (currentStep / steps * 100);
    
    if (currentStep >= steps) {
      clearInterval(progressInterval);
      isVisible.value = false;
    }
  }, updateInterval);
});

onBeforeUnmount(() => {
  if (progressInterval) {
    clearInterval(progressInterval);
  }
});
</script>

<script lang="ts">
export default {
  name: 'ToastComponent'
};
</script>
<style scoped>
.toast {
  position: relative;
  top: 0;
  right: 0;
  left: 0;
  min-width: auto;
  max-width: 100%;
  background: var(--toast-bg, var(--colour-surface-cont-lowest, #ffffff));
  border: 1px solid var(--toast-border, var(--colour-surface-cont, #dbdbdb));
  border-radius: 8px;
  padding: 16px;
  color: var(--toast-text, var(--colour-base-text-colour));
  box-shadow: var(--toast-shadow, 0 8px 28px rgba(0, 0, 0, 0.14));
  z-index: 99999;
  display: flex;
  gap: 12px;
  overflow: hidden;
  opacity: 0;
  transform: translateY(-20px);
  pointer-events: auto;
  margin-bottom: 16px;
}

.toast.slide-in {
  animation: slideIn 0.3s forwards;
}

.toast.toast--centered {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 100010;
  min-width: min(360px, calc(100vw - 32px));
  max-width: min(360px, calc(100vw - 32px));
  margin-bottom: 0;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.toast.toast--centered.slide-in {
  animation: toastCenterIn 0.25s forwards;
}

.toast.success {
  border-left: 4px solid var(--colour-green-button);
}

.toast.error {
  border-left: 4px solid var(--colour-red-text-colour);
}

.toast.info {
  border-left: 4px solid var(--bg-info);
}

.toast-icon {
  font-size: 20px;
  display: flex;
  align-items: center;
}

.toast-content {
  flex: 1;
}

.toast-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.toast-message {
  font-size: 14px;
  color: var(--toast-message, var(--colour-secondary-text-colour));
}

.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: var(--colour-green-button);
  transition: width 0.1s linear;
}

.toast.error .progress-bar {
  background: var(--colour-red-text-colour);
}

.toast.info .progress-bar {
  background: var(--bg-info, var(--colour-orange-button));
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes toastCenterIn {
  from {
    opacity: 0;
    transform: translate(-50%, calc(-50% - 12px));
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}
</style>
